// Error vocabulary for the PMS layer. Pure; nothing here throws for an
// expected vendor failure — failures are values so there is exactly one path
// through classification (see classify.ts) and one place that decides what to
// do about them (see runner.ts).

/**
 * How the job runner must treat a failure.
 *
 * AMBIGUOUS is the important one: the request may or may not have landed
 * (timeout, 5xx, no response body). SSM has no idempotency key, so for a
 * payment this can never be retried automatically — posting twice shows the
 * facility money they never received. Promoting "we don't know" to a
 * classification makes that rule a single testable branch instead of an `if`
 * buried inside retry policy.
 */
export type PmsErrorClass =
  | 'RETRYABLE' // transient; back off and try again
  | 'RETRYABLE_WINDOW' // blocked for a while (day-end, daily limit); retry much later
  | 'AMBIGUOUS' // may have landed; a human decides
  | 'CONFLICT' // vendor state disagrees with ours
  | 'NOT_FOUND' // our stored external id is stale
  | 'VALIDATION' // our bug or bad config; retrying fails identically
  | 'BUSINESS_BLOCK' // vendor refuses for a business reason; needs a person
  | 'AUTH' // credentials/permissions; every job will fail until fixed
  | 'PARTIAL' // vendor reports an inconsistent half-applied state
  | 'SKIPPED'; // never attempted (shadow mode, or the vendor cannot do this op)

export type PmsErrorKind =
  | 'http' // non-2xx with no usable body
  | 'application' // 2xx whose embedded Error object reports failure
  | 'network' // never reached the vendor, or the response failed validation
  | 'ours'; // we rejected it before sending

export interface PmsError {
  class: PmsErrorClass;
  kind: PmsErrorKind;
  message: string;
  httpStatus?: number;
  /** SSM: 1001 Invalid Request, 1002 Validation Error, 1003 Exception, 1004 DayEnd. */
  errorCode?: number;
  /** Floor for the next attempt, when the vendor implies a wait. */
  retryAfterMs?: number;
  raw?: unknown;
}

export function pmsError(error: PmsError): PmsError {
  return error;
}

/**
 * Thrown when a built vendor request contains a field we must never send.
 * This is a programming error, not a vendor failure — it must break loudly in
 * tests rather than degrade into a classified result.
 */
export class PmsGuardError extends Error {
  violations: string[];

  constructor(violations: string[]) {
    super(`Refusing to send request; forbidden fields present: ${violations.join(', ')}`);
    this.name = 'PmsGuardError';
    this.violations = violations;
    // Required for `instanceof` to work with target: es5.
    Object.setPrototypeOf(this, PmsGuardError.prototype);
  }
}
